/*
  Real skin stories (sections/skin-stories.liquid). Forge Together, 24 September 2026. Draft on development theme 199659389315.
  Before-and-after compare (drag, optional hover on desktop, keyboard through a hidden range input), story switching
  with a tab rail, and product and type filters. No dependencies.
*/
if (!customElements.get('skin-stories')) {
  class SkinStories extends HTMLElement {
    connectedCallback() {
      this.stories = Array.from(this.querySelectorAll('[data-story]'));
      if (!this.stories.length) return;
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.rail = this.querySelector('.ss__rail');
      this.nav = this.querySelector('.ss__nav');
      this.live = this.querySelector('[data-live]');
      this.start = Number(this.dataset.start) || 50;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.hoverMode = this.dataset.mode === 'hover' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      this.filters = { product: '', type: '' };
      this.current = 0;

      this.stories.forEach((story) => {
        const compare = story.querySelector('[data-compare]');
        if (compare) this.setupCompare(compare);
      });

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index, true));
        tab.addEventListener('keydown', (event) => this.onTabKey(event, index));
      });
      this.querySelectorAll('[data-step]').forEach((button) =>
        button.addEventListener('click', () => this.step(Number(button.dataset.step)))
      );
      this.querySelectorAll('[data-filter-product]').forEach((button) =>
        button.addEventListener('click', () => this.setFilter('product', button.dataset.filterProduct, button))
      );
      this.querySelectorAll('[data-filter-type]').forEach((button) =>
        button.addEventListener('click', () => this.setFilter('type', button.dataset.filterType, button))
      );

      const target = window.location.hash.slice(1);
      const linked = target ? this.stories.findIndex((story) => story.id === target) : -1;
      if (linked > 0) this.select(linked, false);
      this.updateCount();
      this.observeFirstView();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      clearTimeout(this.hintTimer);
    }

    // Compare -----------------------------------------------------------------------------------------

    setupCompare(el) {
      const range = el.querySelector('.ss__range');
      const set = (value, animate) => {
        const pos = Math.min(100, Math.max(0, value));
        el.classList.toggle('is-animating', Boolean(animate) && !this.reduced);
        el.style.setProperty('--pos', `${pos}%`);
        if (range) {
          range.value = String(Math.round(pos));
          range.setAttribute('aria-valuetext', `${Math.round(pos)}%`);
        }
        el.dataset.near = pos < 12 ? 'before' : pos > 88 ? 'after' : '';
      };
      el.ssSet = set;

      const fromEvent = (event) => {
        const rect = el.getBoundingClientRect();
        return ((event.clientX - rect.left) / rect.width) * 100;
      };

      let pressed = false;
      let dragging = false;
      let startX = 0;
      let startY = 0;

      el.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || event.target.closest('[data-spot]')) return;
        pressed = true;
        dragging = event.pointerType === 'mouse';
        startX = event.clientX;
        startY = event.clientY;
        if (dragging) {
          el.setPointerCapture(event.pointerId);
          el.classList.add('is-dragging');
          set(fromEvent(event), true);
          this.interacted(el);
        }
      });

      el.addEventListener('pointermove', (event) => {
        if (pressed) {
          if (!dragging) {
            // Touch: wait until the gesture is clearly sideways, so a vertical swipe still scrolls the page.
            const dx = Math.abs(event.clientX - startX);
            const dy = Math.abs(event.clientY - startY);
            if (dx < 6 && dy < 6) return;
            if (dy > dx) {
              pressed = false;
              return;
            }
            dragging = true;
            el.setPointerCapture(event.pointerId);
            el.classList.add('is-dragging');
            this.interacted(el);
          }
          set(fromEvent(event), false);
        } else if (this.hoverMode && event.pointerType === 'mouse' && !event.target.closest('[data-spot]')) {
          set(fromEvent(event), false);
          this.interacted(el);
        }
      });

      const end = (event) => {
        if (pressed && !dragging && event.type === 'pointerup') {
          // A tap on touch moves the divider to the tapped point.
          set(fromEvent(event), true);
          this.interacted(el);
        }
        pressed = false;
        dragging = false;
        el.classList.remove('is-dragging');
      };
      el.addEventListener('pointerup', end);
      el.addEventListener('pointercancel', end);

      if (this.hoverMode) {
        el.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'mouse' && !pressed) set(this.start, true);
        });
      }

      const spot = el.querySelector('[data-spot]');
      if (spot) this.setupSpot(spot);

      if (range) {
        range.addEventListener('input', () => {
          set(Number(range.value), false);
          this.interacted(el);
        });
      }
    }

    // The product point: hover shows the card on desktop (CSS); a tap or click toggles it, for touch and keyboard.
    setupSpot(spot) {
      const dot = spot.querySelector('.ss__spot-dot');
      const close = () => {
        spot.classList.remove('is-open');
        dot.setAttribute('aria-expanded', 'false');
      };
      dot.addEventListener('click', (event) => {
        event.stopPropagation();
        const open = !spot.classList.contains('is-open');
        this.querySelectorAll('[data-spot].is-open').forEach((other) => other !== spot && other.classList.remove('is-open'));
        spot.classList.toggle('is-open', open);
        dot.setAttribute('aria-expanded', String(open));
      });
      spot.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          close();
          dot.focus();
        }
      });
      document.addEventListener('click', (event) => {
        if (!spot.contains(event.target)) close();
      });
    }

    interacted(el) {
      el.dataset.touched = 'true';
      el.classList.remove('show-hint');
      clearTimeout(this.hintTimer);
      this.hintTimer = null;
    }

    // A short nudge the first time the section comes into view, so it's clear the divider moves.
    observeFirstView() {
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.playHint(this.stories[this.current]);
        },
        { threshold: 0.45 }
      );
      const first = this.stories[this.current].querySelector('[data-compare]');
      if (first) this.observer.observe(first);
    }

    playHint(story) {
      const el = story && story.querySelector('[data-compare]');
      if (!el || el.dataset.touched) return;
      const hint = el.querySelector('.ss__hint');
      if (hint) {
        hint.textContent = this.hoverMode ? this.dataset.hintHover : this.dataset.hintDrag;
        el.classList.add('show-hint');
      }
      if (this.reduced) return;
      const steps = [
        [350, this.start - 14],
        [1050, this.start + 10],
        [1750, this.start],
      ];
      steps.forEach(([delay, value]) =>
        setTimeout(() => {
          if (!el.dataset.touched) el.ssSet(value, true);
        }, delay)
      );
      this.hintTimer = setTimeout(() => el.classList.remove('show-hint'), 4200);
    }

    // Stories -----------------------------------------------------------------------------------------

    visible() {
      return this.stories.map((_, index) => index).filter((index) => !this.stories[index].hasAttribute('data-filtered-out'));
    }

    select(index, fromUser) {
      if (!this.visible().includes(index)) return;
      this.querySelectorAll('[data-spot].is-open').forEach((spot) => spot.classList.remove('is-open'));
      this.current = index;
      this.stories.forEach((story, i) => {
        const on = i === index;
        story.classList.toggle('is-active', on);
        story.toggleAttribute('inert', !on);
        story.setAttribute('aria-hidden', String(!on));
        const range = story.querySelector('.ss__range');
        if (range) range.tabIndex = on ? 0 : -1;
      });
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });

      const compare = this.stories[index].querySelector('[data-compare]');
      if (compare && !compare.dataset.touched) compare.ssSet(this.start, false);

      const tab = this.tabs[index];
      if (tab && this.rail) {
        const left = tab.offsetLeft - (this.rail.clientWidth - tab.clientWidth) / 2;
        this.rail.scrollTo({ left: Math.max(0, left), behavior: this.reduced ? 'auto' : 'smooth' });
      }

      this.updateCount();
      if (fromUser) {
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', `#${this.stories[index].id}`);
        }
        this.announce();
      }
    }

    step(direction) {
      const list = this.visible();
      if (list.length < 2) return;
      const position = list.indexOf(this.current);
      const next = list[(position + direction + list.length) % list.length];
      this.select(next, true);
    }

    onTabKey(event, index) {
      const list = this.visible();
      const position = list.indexOf(index);
      let next = null;
      if (event.key === 'ArrowRight') next = list[(position + 1) % list.length];
      if (event.key === 'ArrowLeft') next = list[(position - 1 + list.length) % list.length];
      if (event.key === 'Home') next = list[0];
      if (event.key === 'End') next = list[list.length - 1];
      if (next === null || next === undefined) return;
      event.preventDefault();
      this.select(next, true);
      this.tabs[next].focus();
    }

    setFilter(kind, value, button) {
      this.filters[kind] = value;
      button.parentElement.querySelectorAll('button').forEach((other) => {
        other.setAttribute('aria-pressed', String(other === button));
      });

      const matches = (node) =>
        (!this.filters.product || node.dataset.product === this.filters.product) &&
        (!this.filters.type || node.dataset.type === this.filters.type);

      this.stories.forEach((story) => story.toggleAttribute('data-filtered-out', !matches(story)));
      this.tabs.forEach((tab) => {
        tab.hidden = !matches(tab);
      });

      this.querySelectorAll('[data-product-intro]').forEach((intro) => {
        intro.hidden = intro.dataset.productIntro !== this.filters.product;
      });

      const list = this.visible();
      if (list.length && !list.includes(this.current)) this.select(list[0], true);
      else this.updateCount();
    }

    updateCount() {
      const list = this.visible();
      const pad = (number) => String(number).padStart(2, '0');
      const story = this.stories[this.current];
      const counter = story && story.querySelector('[data-story-number]');
      if (counter) counter.textContent = `${pad(list.indexOf(this.current) + 1)} / ${pad(list.length)}`;
      if (this.nav) this.nav.toggleAttribute('data-single', list.length < 2);
    }

    announce() {
      if (!this.live) return;
      const story = this.stories[this.current];
      const title = story.querySelector('.ss__title, .ss__quote-name');
      const list = this.visible();
      this.live.textContent = `${list.indexOf(this.current) + 1} / ${list.length}: ${title ? title.textContent.trim() : ''}`;
    }
  }

  customElements.define('skin-stories', SkinStories);
}
